import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Cookie, Lock, Eye, FileText, Globe } from 'lucide-react';
import { Card } from '../components/ui/card';

const Section = ({ icon: Icon, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="mb-8"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground">{title}</h2>
    </div>
    <div className="text-muted-foreground space-y-4">{children}</div>
  </motion.div>
);

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - VisualMathLab</title>
        <meta name="description" content="VisualMathLab privacy policy. Learn how we collect, use, and protect your personal information." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://visualmathlab.online/privacy" />
      </Helmet>

      <div className="min-h-screen py-20 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 2025
            </p>
          </motion.div>

          <Card className="p-8 md:p-12">
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <p className="text-muted-foreground leading-relaxed">
                At VisualMathLab, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you visit our website.
              </p>
            </motion.div>

            {/* Information We Collect */}
            <Section icon={FileText} title="Information We Collect">
              <p>
                We collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Name and email address when you contact us</li>
                <li>Messages and communications you send to us</li>
                <li>Calculator inputs and preferences (stored locally in your browser)</li>
              </ul>
              <p className="mt-4">
                We also automatically collect certain information when you visit our site:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Browser type and version</li>
                <li>Device information and operating system</li>
                <li>IP address and general location data</li>
                <li>Pages visited and time spent on our site</li>
                <li>Referring website addresses</li>
              </ul>
            </Section>

            {/* Cookies */}
            <Section icon={Cookie} title="Cookies and Tracking Technologies">
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our site. 
                Cookies are small text files stored on your device that help us:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Enable basic site functionality</li>
                <li><strong>Preference Cookies:</strong> Remember your settings (theme, language)</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Third-Party Cookies:</strong> From services like Google Analytics to analyze traffic</li>
              </ul>
              <p className="mt-4">
                You can control cookie preferences through your browser settings or our cookie consent banner.
              </p>
            </Section>

            {/* How We Use Information */}
            <Section icon={Eye} title="How We Use Your Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide, maintain, and improve our calculators and services</li>
                <li>Respond to your questions, comments, and support requests</li>
                <li>Analyze usage patterns to enhance user experience</li>
                <li>Send updates about our services (with your consent)</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </Section>

            {/* Data Storage */}
            <Section icon={Lock} title="Data Storage and Security">
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <p className="mt-4">
                Most of your calculator data is stored locally in your browser and never transmitted to 
                our servers. This ensures your mathematical work remains private.
              </p>
              <p className="mt-4">
                However, no method of transmission over the internet is 100% secure. While we strive to 
                protect your data, we cannot guarantee absolute security.
              </p>
            </Section>

            {/* Third-Party Services */}
            <Section icon={Globe} title="Third-Party Services">
              <p>
                We may use third-party services that collect, monitor, and analyze information to help 
                us improve our service:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google Analytics:</strong> To understand how visitors interact with our site</li>
                <li><strong>Content Delivery Networks (CDNs):</strong> To ensure fast loading times</li>
              </ul>
              <p className="mt-4">
                These third parties have their own privacy policies. We encourage you to review them.
              </p>
            </Section>

            {/* Your Rights */}
            <Section icon={Shield} title="Your Privacy Rights">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your information</li>
                <li>Withdraw consent for data processing</li>
                <li>Opt-out of marketing communications</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at: <a href="mailto:privacy@visualmathlab.online" className="text-primary hover:underline">privacy@visualmathlab.online</a>
              </p>
            </Section>

            {/* Children's Privacy */}
            <Section icon={Shield} title="Children's Privacy">
              <p>
                Our service is available to users of all ages. We do not knowingly collect personally 
                identifiable information from children without parental consent. If you are a parent or 
                guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </Section>

            {/* Changes to Policy */}
            <Section icon={FileText} title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new policy on this page and updating the "Last Updated" date.
              </p>
              <p className="mt-4">
                We encourage you to review this policy periodically for any changes.
              </p>
            </Section>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mt-12 p-6 bg-muted/50 rounded-xl"
            >
              <h3 className="text-lg font-semibold text-foreground mb-2">Contact Us</h3>
              <p className="text-muted-foreground mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  <strong>Email:</strong> <a href="mailto:privacy@visualmathlab.online" className="text-primary hover:underline">privacy@visualmathlab.online</a>
                </p>
                <p className="text-sm">
                  <strong>Website:</strong> <a href="https://visualmathlab.online" className="text-primary hover:underline">https://visualmathlab.online</a>
                </p>
              </div>
            </motion.div>
          </Card>
        </div>
      </div>
    </>
  );
}